import React from "react";
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { PullVoucherRequest, Voucher } from "../../../../../models/Vouchers/Voucher";
import AppButton from "../../../../../views/components/common/buttons/AppButton";

interface VouchersRequestsTableProp {
  maxHeight: number;
  pullVouchersRequests: PullVoucherRequest[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  onCheckStatus: (id: number, requestId: string) => void;
}

const VouchersRequestsTable = ({
  maxHeight,
  pullVouchersRequests,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onCheckStatus,
}: VouchersRequestsTableProp) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {pullVouchersRequests.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                      minWidth: "60px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Request ID" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Attempts" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Completed" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Success" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Failed" fontSize="0.8rem" />
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "created_at"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "created_at",
                          sortBy === "created_at" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_at")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Requested At
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Actions" fontSize="0.8rem" />
                </TableRow>
              </TableHead>
              <TableBody>
                {pullVouchersRequests.map((pullVoucherRequest) => {
                  return (
                    <TableRow
                      key={pullVoucherRequest.id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 0,
                          position: "sticky",
                          zIndex: 15,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          color:
                            theme.palette.customColors?.greyTextColor ||
                            theme.palette.text.secondary,
                          minWidth: "60px",
                        }}
                      >
                        {pullVoucherRequest.id || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {pullVoucherRequest.request_id || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {pullVoucherRequest.attempts || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {Boolean(pullVoucherRequest.completed) ? "Yes" : "No"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {Boolean(pullVoucherRequest.success) ? "Yes" : "No"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {Boolean(pullVoucherRequest.failed) ? "Yes" : "No"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {pullVoucherRequest.created_at
                          ? formatDate(pullVoucherRequest.created_at)
                          : "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {!pullVoucherRequest.completed && (
                          <AppButton
                            variant="primary"
                            label="Check Status"
                            onClick={() =>
                              onCheckStatus(
                                pullVoucherRequest.id,
                                pullVoucherRequest.request_id
                              )
                            }
                          />
                        )}
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default VouchersRequestsTable;
